<template>
  <div class="product-form">
    <form
      class="product-form__form"
      @submit.prevent="addToCartValidation"
    >
      <div>
        <template v-if="!showPreOrderConfirm">
          <overlay
            key="mobileSizeSelector"
            block-class="mobile-size-selector-overlay"
            direction="up"
            namespace="mobileSizeSelector"
            :template="false"
            type="shelf"
          >
            <template #body="{ isActive }">
              <mobile-size-selector
                v-if="!showMobileNotifyMe"
                v-show="getComponentVisible('mobile-size-selector')"
                ref="mobileSizeSelector"
                handle="mobile.handle"
                :is-active="isActive"
                :product="product"
                :size-order="sizeOrder"
                :show-size-guide="showSizeGuide"
                :size-guide-fit-label="productFit.fitLabel"
                :is-size-guide-available="isSizeGuideAvailable"
                :store-availability-content="storeAvailabilityContent"
                :use-store-availability-from-locations="useStoreAvailabilityFromLocations"
                :coordinates="coordinates"
                :variant-availability-data="variantAvailabilityData"
                :low-availability-threshold="lowAvailabilityThreshold"
                :gift-wrapping="showGiftWrapping"
                @show-mobile-notify="handleNotifyUpdate"
                @size-selector-close="() => showMobileSizeSelector = false"
                @size-selector-action="handleSizeSelectorAction"
              />
            </template>
          </overlay>
          <div
            v-if="!isMobile"
            class="product-form__price-container"
          >
            <product-prices
              :available="product?.available"
              block-class="main-product"
              :compare-at-price="product?.compareAtPrice?.amount"
              :price="product?.price?.amount || liquid.price.amount"
              reverse
            />
          </div>

          <!-- Product swatches -->
          <product-swatches
            v-if="!isMobile"
            block-class="product-form"
            load-swatches
            :product="product"
          />

          <div
            class="form-group__field product-form__size-selector"
            :class="[{'form-group__split': comingSoonProduct }]"
          >
            <span
              v-if="comingSoonProduct"
              class="coming-soon__text"
              v-text="$string('product.coming_soon.info')"
            />
            <size-selector
              v-if="!isMobile && !hasOnlyDefaultVariant"
              ref="sizeSelector"
              :product="product"
              :size-order="sizeOrder"
              :size-guide-fit-label="productFit.fitLabel"
              :is-size-guide-available="isSizeGuideAvailable"
              :variant="variant"
              :coming-soon="comingSoon"
              :low-availability-threshold="lowAvailabilityThreshold"
              coming-soon-product
              @coming-soon-hover="showComingSoonMessage"
              @size-selector-action="handleSizeSelectorAction"
            />
          </div>

          <!-- Size guide component. -->
          <teleport to="body">
            <overlay
              key="sizeGuide"
              block-class="size-guide-overlay"
              namespace="sizeGuide"
              type="drawer"
              direction="left"
            >
              <template
                #title
              >
                <span
                  v-text="sizeGuideDrawerTitle"
                />
              </template>

              <template #body="{ isActive }">
                <div
                  v-if="productFit.fitText"
                  class="size-guide-overlay__fit sub-menu"
                >
                  <h2
                    class="
                    size-guide-overlay__fit-title
                    text-desktop-14-desktop
                    text-mobile-14-mobile"
                  >
                    <span
                      class="text-regular"
                      v-text="sizeGuideFitTitle"
                    />
                  </h2>
                  <div
                    class="rte"
                    v-html="productFitFitTextHtml"
                  />
                </div>

                <size-guide
                  v-show="getComponentVisible('size-guide') && !hasOneVariant()"
                  :is-active="isActive"
                  :product="product"
                  :show-size-guide="showSizeGuide"
                  :variant="variant"
                  @size-guide-action="handleSizeSelectorAction"
                />
              </template>
            </overlay>
          </teleport>

          <teleport to="body">
            <overlay
              key="storeAvailability"
              block-class="store-availability-overlay"
              direction="left"
              namespace="storeAvailability"
              type="drawer"
            >
              <template
                #title
              >
                <span
                  v-text="$string('product.store_availability.title')"
                />
              </template>
              />
              <template #body>
                <store-availability
                  class="store-availability__component"
                  :product="product"
                  :store-availability-content="storeAvailabilityContent"
                  :use-store-availability-from-locations="useStoreAvailabilityFromLocations"
                  :coordinates="coordinates"
                  :variant-availability-data="variantAvailabilityData"
                  @store-availability-action="handleSizeSelectorAction"
                />
              </template>
            </overlay>
          </teleport>

          <!-- Back in stock component. -->
          <teleport to="body">
            <overlay
              key="backInStock"
              block-class="back-in-stock-overlay"
              namespace="backInStock"
              type="drawer"
              direction="left"
            >
              <template
                #title
              >
                <span
                  v-text="buildNotifyString"
                />
              </template>

              <template #body>
                <p
                  class="back-in-stock-overlay__text text-desktop-14 text-mobile-14 "
                  v-text="$string('product.back_in_stock.text')"
                />

                <back-in-stock
                  :product="product"
                  :variant="variant"
                />

                <store-availability
                  v-if="storeAvailabilityContent?.show && isMobile"
                  :store-availability-content="storeAvailabilityContent"
                  :use-store-availability-from-locations="useStoreAvailabilityFromLocations"
                  :coordinates="coordinates"
                  :variant-availability-data="variantAvailabilityData"
                />
              </template>

              <template
                v-if="storeAvailabilityContent?.show && !isMobile"
                #footer
              >
                <store-availability
                  :store-availability-content="storeAvailabilityContent"
                  :use-store-availability-from-locations="useStoreAvailabilityFromLocations"
                  :coordinates="coordinates"
                  :variant-availability-data="variantAvailabilityData"
                />
              </template>
            </overlay>
          </teleport>

          <!-- Coming soon component. -->
          <teleport to="body">
            <overlay
              v-if="comingSoonProduct"
              key="comingSoon"
              block-class="coming-soon-overlay"
              namespace="comingSoon"
              type="drawer"
              direction="left"
            >
              <template
                #title
              >
                <span
                  v-text="$string('product.coming_soon.notify_me')"
                />
              </template>

              <template #body="">
                <p
                  class="coming-soon-overlay__text text-desktop-14 text-mobile-14 "
                  v-text="$string('product.coming_soon.text')"
                />

                <coming-soon
                  :coming-soon-list-id="comingSoon.listId"
                />
              </template>
            </overlay>
          </teleport>

          <!-- Private Sale component. -->
          <teleport to="body">
            <overlay
              v-if="privateSaleProduct"
              key="privateSale"
              block-class="private-sale-overlay"
              namespace="privateSale"
              type="drawer"
              direction="left"
            >
              <template
                #title
              >
                <span
                  v-text="$string('product.private_sale.notify_me')"
                />
              </template>

              <template #body="">
                <p
                  class="private-sale-overlay__text text-desktop-14 text-mobile-14 "
                  v-text="$string('product.private_sale.text')"
                />

                <private-sale
                  :private-sale-list-id="privateSale.listId"
                />
              </template>
            </overlay>
          </teleport>

          <!-- Displayed when the BIS notification UI is NOT displayed. -->
          <div
            v-if="!showMobileNotifyMe"
            class="product-form__cta form-group__footer"
          >
            <!-- Desktop specific components -->
            <template v-if="!isMobile">
              <btn
                v-if="preOrderProduct"
                class="product-form__add-to-cart main-product__add-to-cart depict-add-to-cart"
                block-class="product-form"
                :label="addToCartText"
                add-to-cart-button
                :button-message="$string('product.pre_order.pre_order_info')"
                :show-text-icon="false"
                type="button"
                @message-hovered="showPreOrderMessage"
                @click="addToCartValidation"
              />

              <btn
                v-else-if="comingSoonProduct"
                class="
                  product-form__coming-soon
                  main-product__coming-soon
                "
                block-class="product-form"
                :label="$string('product.cta.coming_soon')"
                :show-text-icon="false"
                @click="handleComingSoonClick($event)"
              />

              <btn
                v-else-if="privateSaleProduct"
                class="
                  product-form__private-sale
                  main-product__private-sale
                "
                block-class="product-form"
                :label="$string('product.cta.private_sale')"
                :show-text-icon="false"
                @click="handlePrivateSaleClick($event)"
              />

              <!-- Non preorder add-to-cart, displays when variant is selected. -->
              <btn
                v-else-if="formFooterState === 'addToCart'"
                class="
                  product-form__add-to-cart
                  product-form__add-to-cart--purchasable
                  main-product__add-to-cart
                  depict-add-to-cart
                "
                :class="{
                  ['product-form__add-to-cart--active']: product?.hasOnlyDefaultVariant || variant
                }"
                block-class="product-form"
                :label="addToCartText"
                add-to-cart-button
                :hover-text="addToCartHoverText"
                :show-text-icon="false"
                type="submit"
                @message-hovered="showShippingMessage"
              />

              <btn
                v-else-if="formFooterState === 'notifyMe'"
                class="
                  product-form__notify-me
                  main-product__notify-me
                "
                block-class="product-form"
                :label="$string('product.cta.notify_me')"
                :show-text-icon="false"
                @click="handleNotifyMeClick($event)"
              />
            </template>

            <!-- Mobile specific components -->
            <template v-else>
              <!-- Mobile coming soon form -->
              <btn
                v-if="comingSoonProduct"
                class="
                  product-form__coming-soon
                  main-product__coming-soon
                "
                block-class="product-form"
                :label="$string('product.cta.coming_soon_mobile')"
                :show-text-icon="false"
                @click="handleComingSoonClick($event)"
              />

              <!-- Mobile coming soon form -->
              <btn
                v-else-if="privateSaleProduct"
                class="
                  product-form__private-sale
                  main-product__private-sale
                "
                block-class="product-form"
                :label="$string('product.cta.private_sale')"
                :show-text-icon="false"
                @click="handlePrivateSaleClick($event)"
              />

              <!-- Mobile preorder add to cart button, variant selected -->
              <btn
                v-else-if="formFooterState === 'addToCart' && preOrderProduct"
                class="product-form__add-to-cart main-product__add-to-cart depict-add-to-cart"
                block-class="product-form"
                :label="mobileAddToCartText"
                add-to-cart-button
                :show-text-icon="false"
                type="button"
                :validation-state="mobileAddState"
                @click="addToCartValidation"
              />

              <!-- Mobile add to cart button, variant selected and not preorder -->
              <btn
                v-else-if="formFooterState === 'addToCart' && hasOneVariant()"
                class="product-form__add-to-cart main-product__add-to-cart depict-add-to-cart"
                :class="[
                  {
                    'product-form__add-to-cart--active': shippingHover,
                  },
                ]"
                block-class="product-form"
                :label="mobileAddToCartText"
                add-to-cart-button
                :show-text-icon="false"
                type="button"
                mobile-message-click-active
                @message-clicked="handleMobileButtonMessageClick"
                @click="addToCartValidation"
              />

              <template v-else-if="formFooterState === 'selectSize' || !hasOneVariant()">
                <!-- Add to cart button that displays at foot of open mobile size selector -->
                <btn
                  v-if="showMobileSizeSelector"
                  class="product-form__add-to-cart main-product__add-to-cart depict-add-to-cart"
                  block-class="product-form"
                  :label="mobileAddToCartText"
                  add-to-cart-button
                  :show-text-icon="false"
                  type="submit"
                  :validation-state="mobileAddState"
                />

                <!-- Add to cart button that displays at foot of closed mobile size selector -->
                <btn
                  v-else
                  class="product-form__add-to-cart main-product__add-to-cart depict-add-to-cart"
                  block-class="product-form"
                  :label="hasOneVariant ? mobileAddToCartText : mobileSelectSizeText"
                  mobile-message-click-active
                  add-to-cart-button
                  :show-text-icon="false"
                  type="button"
                  @message-clicked="handleMobileButtonMessageClick"
                  @click="openOverlay({
                    component: 'mobile-size-selector',
                    ignoreDismissed: true,
                    namespace: 'mobileSizeSelector',
                  })"
                />
              </template>
            </template>

            <div class="product-form__wishlist">
              <wishlist-button :product="product" />
            </div>
          </div>

          <div
            v-else
            class="product-form__mobile-notify form-group__footer"
          >
            <div
              class="
                product-form__mobile-notify-header
                main-product__form-footer-action
              "
            >
              <p
                class="text-mobile-small-uc-mobile"
                v-text="buildNotifyString"
              />
            </div>

            <back-in-stock
              :product="product"
              :variant="notifyVariant"
            />

            <button
              class="
              text-mobile-small-uc-mobile
              product-form__mobile-notify-action
              main-product__form-footer-action
            "
              type="button"
              @click="() => handleNotifyUpdate(null, false)"
              v-text="$string('product.back_in_stock.back_to_select')"
            />

            <div class="product-form__wishlist">
              <wishlist-button :product="product" />
            </div>
          </div>
        </template>

        <template v-else>
          <div class="product-form__preorder">
            <div class="product-form__preorder-header">
              <span
                class="
                  text-desktop-small-uc-desktop
                  text-mobile-small-uc-mobile
                "
                v-text="$string('product.pre_order.confirmation')"
              />
            </div>

            <button
              class="
                main-product__form-footer-action
                product-form__preorder-action
                product-form__preorder-action--cancel
              "
              type="button"
              @click="showPreOrderConfirm = false"
              v-text="$string('product.pre_order.cancel')"
            />
            <button
              class="
                main-product__form-footer-action
                product-form__preorder-action
                product-form__preorder-action--confirm
              "
              type="button"
              @click="emptyCartAddProduct"
              v-text="confirmationLabelText"
            />
          </div>
        </template>

        <!-- PDP Form preorder info -->
        <div
          v-if="!isMobile && preOrderHover && preorder?.contentAvailable"
          class="product-form__shipping rte"
        >
          <p
            class="text-desktop-small product-form__shipping-text"
            v-text="preorder.content?.text"
          />

          <ul v-if="preorder?.content?.bullets?.length">
            <li
              v-for="(bullet, index) of preorder?.content.bullets"
              :key="`preorder-bullet-${index}`"
              class="text-desktop-small"
              v-text="bullet"
            />
          </ul>
        </div>

        <!-- PDP Form coming soon info -->
        <div
          v-if="
            !isMobile &&
              comingSoonHover &&
              comingSoon.contentAvailable
          "
          class="product-form__shipping rte"
        >
          <p
            class="text-desktop-small product-form__shipping-text"
            v-text="comingSoon.content?.text"
          />

          <ul v-if="comingSoon?.content?.bullets?.length">
            <li
              v-for="(bullet, index) of comingSoon?.content.bullets"
              :key="`preorder-bullet-${index}`"
              class="text-desktop-small"
              v-text="bullet"
            />
          </ul>
        </div>
      </div>
    </form>

    <div
      v-if="cartError && cartError.description"
      class="product-form__error text-desktop-regular-desktop text-mobile-regular-mobile"
      v-text="cartError.description"
    />
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Object} content - Content to be displayed.
 * @param {Boolean} liquid.hasOnlyDefaultVariant - Has only default variant.
 * @param {Object} preorder - Object containing preorder details.
 * @param {Object} comingSoon - Object containing coming soon details.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} productFit - Object containing product fit details.
 * @param {Array} sizeOrder - Array of sizes.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Object} storeAvailabilityContent - Store availability settings.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import BackInStock from '~async/back-in-stock/back-in-stock'
import ComingSoon from '~async/coming-soon/coming-soon'
import Btn from '~global/btn/btn'
import Overlay from '~global/overlay/overlay'
import SizeGuide from '~async/size-guide/size-guide'
import SizeSelector from '~async/size-selector/size-selector'
import MobileSizeSelector from '~async/mobile-size-selector/mobile-size-selector'
import PrivateSale from '~async/private-sale/private-sale'
import ProductPrices from '~global/product-prices/product-prices'
import ProductSwatches from '~async/product-swatches/product-swatches'
import WishlistButton from '~async/wishlist-button/wishlist-button'
import StoreAvailability from '~async/store-availability/store-availability'

export default {
  name: 'ProductForm',

  components: {
    BackInStock,
    ComingSoon,
    Btn,
    MobileSizeSelector,
    Overlay,
    PrivateSale,
    ProductPrices,
    ProductSwatches,
    SizeGuide,
    SizeSelector,
    WishlistButton,
    StoreAvailability,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        hasOnlyDefaultVariant: false,
      }),
    },

    showGiftWrapping: {
      type: Boolean,
      default: false,
    },

    preorder: {
      type: Object,
      default: () => ({
        contentAvailable: false,
        content: {
          bullets: [],
          text: null,
        },
        isPreorderProduct: false,
      }),
    },

    comingSoon: {
      type: Object,
      default: () => ({
        contentAvailable: false,
        content: {
          bullets: [],
          text: null,
        },
        listId: null,
        isComingSoonProduct: false,
      }),
    },

    privateSale: {
      type: Object,
      default: () => ({
        listId: null,
        isPrivateSaleProduct: false,
      }),
    },

    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },

    productFit: {
      type: Object,
      default: () => ({
        fitLabel: '',
        fitText: '',
      }),
    },

    sizeOrder: {
      type: Array,
      default: () => [],
    },

    storeAvailabilityContent: {
      type: Object,
      default: () => ({}),
    },

    useStoreAvailabilityFromLocations: {
      type: Boolean,
      default: false,
    },

    coordinates: {
      type: Object,
      default: () => ({
        latitude: Number,
        longitude: Number,
      }),
    },

    variantAvailabilityData: {
      type: Array,
      default: () => [],
    },

    lowAvailabilityThreshold: {
      type: Number,
      default: 3,
    },
  },

  emits: {
    'update-variant': (payload) => typeof payload === 'object' || !payload,
    'update-product': (payload) => typeof payload === 'object',
    'size-guide-status-changed': (payload) => typeof payload === 'boolean',
  },

  data() {
    return {
      currentVariantId: this.variant?.id || 0,
      formFooterState: 'addToCart',
      lineItemProperties: {
        '_gift-wrapping': false,
      },
      notifyVariant: null,
      showMobileNotifyMe: false,
      quantity: 1,
      preOrderHover: false,
      shippingHover: false,
      comingSoonHover: false,
      showMobileSizeSelector: false,
      showPreOrderConfirm: false,
      showSizeGuide: false,
      state: 'ready',
      timeout: {},
      cartError: null,
    }
  },

  computed: {

    productFitFitTextHtml() {
      const parser = new DOMParser()
      const decodedString = parser.parseFromString(this.productFit.fitText, 'text/html').body.textContent
      return decodedString
    },

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
      getRelevantMessaging: 'multi-store/getRelevantMessaging',
      hasPreOrderProduct: 'cart/hasPreOrderProduct',
      isVariantSelected: 'product/isVariantSelected',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart,
      isMobile: (state) => state.index.screen.mobile,
      variant: (state) => state.product.variant,
    }),

    /**
     * Computes whether there is size guide content to display.
     * @returns {Boolean}
     */
    isSizeGuideAvailable() {
      return this.hasOneVariant()
        ? Boolean(this.product?.sizeGuide?.fitTextLong)
        : Boolean(this.product?.sizeGuide?.sizeConversion)
    },

    /**
     * Returns the string to display as the Size Guide Drawer title.
     */
    sizeGuideDrawerTitle() {
      return this.hasOneVariant()
        ? this.$string('product.size_guide.measurements_title')
        : this.$string('product.size_guide.title')
    },

    /**
     * Returns the string to display as the Size Guide secondary title.
     */
    sizeGuideFitTitle() {
      return this.hasOneVariant()
        ? this.$string('product.size_guide.measurement')
        : this.$string('product.size_guide.fit_title')
    },

    /**
     * Returns the string to display on the preorder CTA.
     */
    preOrderText() {
      if (!this.variant) {
        return this.$string('product.pre_order.select_size')
      }

      return this.$string('product.pre_order.pre_order')
    },

    /**
     * Indicates if the current product is a preorder product.
     */
    preOrderProduct() {
      return Boolean(this.preorder?.isPreorderProduct)
    },

    /**
     * Indicates if the current product is a coming soon product.
     */
    comingSoonProduct() {
      return Boolean(this.comingSoon?.isComingSoonProduct)
    },

    /**
     * Indicates if the current product is a private sale product.
     */
    privateSaleProduct() {
      return Boolean(this.privateSale?.isPrivateSaleProduct && this.$variable('customer.loggedIn') !== true)
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      let labelText

      if (!this.preOrderProduct) {
        return this.state === 'added'
          ? this.$string('product.cta.added_to_cart')
          : this.$string('product.cta.add_to_cart')
      }

      switch (this.state) {
        case 'adding':
          labelText = this.$string('product.cta.adding')
          break
        case 'added':
          labelText = this.$string('product.cta.added_to_cart')
          break
        default:
          labelText = this.preOrderText
      }

      return labelText
    },

    /**
     * Builds a string to display when the mobile notify component is displayed.
     * @returns {String}
     */
    buildNotifyString() {
      if (!this.notifyVariant) {
        return this.$string('product.cta.notify_me')
      }

      return `${this.$string('product.cta.notify_me_on')} ${this.notifyVariant?.selectedOptions?.[0]?.name?.toLowerCase()} ${this.notifyVariant?.selectedOptions?.[0]?.value}`
    },

    /**
     * Returns an appropriate string for the confirmation button.
     * @returns {String}
     */
    confirmationLabelText() {
      return this.preOrderProduct
        ? this.preOrderText
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * Builds a string to display on mobile product form when a preorder
     * product.
     * @returns {String}
     */
    mobileSelectSizeText() {
      return this.preOrderProduct
        ? this.$string('product.pre_order.select_size')
        : this.$string('product.cta.mobile_add_to_cart')
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    mobileAddToCartText() {
      if (!this.preOrderProduct) {
        if (!this.variant && !this.hasOneVariant()) {
          return this.$string('product.cta.mobile_select_size')
        }

        if (this.hasOneVariant()) {
          return this.$string('product.cta.mobile_add_to_cart_one_variant')
        }

        return this.state === 'added'
          ? this.$string('product.cta.added_to_cart')
          : this.$string('product.cta.mobile_select_size')
      }

      if (!this.variant) {
        return this.$string('product.pre_order.mobile_select_size')
      }

      let labelText
      switch (this.state) {
        case 'adding':
          labelText = this.$string('product.cta.adding')
          break
        case 'added':
          labelText = this.$string('product.cta.added_to_cart')
          break
        default:
          labelText = this.preOrderText
      }

      return labelText
    },

    /**
     * Returns the formatted product price for addition to the add to cart
     * button.
     * @returns {String}
     */
    mobileButtonMessage() {
      return this.$string('product.form.free_shipping')
    },

    /**
     * Computes the validation state for the mobile add to cart button.
     * @returns {String}
     */
    mobileAddState() {
      return this.variant ? '' : 'pending'
    },

    /**
     * Compute add to cart hover text.
     * @returns {String}
     */
    addToCartHoverText() {
      if (this.state === 'added') {
        return this.$string('product.cta.added_to_cart')
      }

      if (!this.variant.id) {
        return this.$string('product.form.select_a_size')
      }

      return this.$string('product.cta.add_to_cart')
    },

    /**
     * Computes whether to display the shipping message on the pdp.
     * @returns {Boolean}
     */
    displayShipping() {
      if (!this.getRelevantMessaging?.form_content) {
        return false
      }

      if (!this.shippingHover) {
        return false
      }

      return true
    },

    /**
     * Compute has only default variant from Liquid and product objects.
     * @returns {Boolean}
     */
    hasOnlyDefaultVariant() {
      return this.liquid.hasOnlyDefaultVariant || this.product.hasOnlyDefaultVariant
    },
  },

  watch: {

    /**
     * Watch currentVariantId to update Canvas variant when select changes.
     */
    currentVariantId() {
      if (this.variant.id === this.currentVariantId) {
        return
      }

      const variant = this.getVariantFromId(this.currentVariantId)
      this.$emit('update-variant', variant)
      this.cartError = null
    },

    /**
     * When the screen is a mobile viewport, update the formFooterState value..
     */
    isMobile: {
      immediate: true,
      handler(value) {
        if (!value) {

          const variant = this.getVariantFromUrl()
          if (!variant) {
            this.formFooterState = 'addToCart'
            return
          }

          if (!variant?.available) {
            this.formFooterState = 'notifyMe'
          }
          return
        }

        this.formFooterState = 'selectSize'
      },
    },

    /**
     * Watch product to update selected variant when product data is loaded.
     */
    product: {
      immediate: true,
      handler() {
        this.getCurrentVariant()
        this.setLineItemColour()
      },
    },

    /**
     * When the mobile size selector is opened, close info tabs.
     */
    showMobileSizeSelector(value) {
      if (!value) {
        this.showSizeGuide = false
        this.formFooterState = 'selectSize'
        return
      }

      if (this.variant) {
        // this.formFooterState = 'addToCart'
      }
      this.shippingHover = false
    },

    /**
     * When the mobile BIS is opened, close info tabs.
     */
    showMobileNotifyMe(value) {
      if (!value) {
        return
      }
      this.shippingHover = false
    },

    /**
     * Emit the size guide status so we can show/hide the description and
     * wishlist within the parent.
     */
    showSizeGuide(value) {
      this.shippingHover = false
      this.$emit('size-guide-status-changed', value)
    },
  },

  created() {
    this.$store.dispatch('product/setVariant', this.variant)

    try {
      window.dpc.setProductId(`${this.variant?.id}`)
    } catch (err) {
      // dpc.setProductId is probably not available
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
      clearCart: 'cart/clearCart',
    }),

    /**
     * Handles closing select form panels.
     */
    closeFormPanels() {
      this.shippingHover = false
      this.showSizeGuide = false
    },

    /**
     * Sets the colour of the product as a line item property.
     */
    setLineItemColour() {
      const colourTag = this.product?.tags?.find(tag => tag.includes('color:'))
      if (!colourTag) {
        return
      }

      this.lineItemProperties.colour = colourTag.split(':')[1].trim()
    },

    /**
     * Handles the event emitted from click events on the button message.
     */
    handleMobileButtonMessageClick() {
      if (!this.isMobile) {
        return
      }

      this.shippingHover = !this.shippingHover
    },

    /**
     * Handles notify me click
     */
    handleNotifyMeClick(event) {
      event.preventDefault()

      this.openOverlay({
        component: 'back-in-stock',
        ignoreDismissed: true,
        namespace: 'backInStock',
      })
    },

    /**
     * Handles coming soon click
     */
    handleComingSoonClick(event) {
      event.preventDefault()

      this.openOverlay({
        component: 'coming-soon',
        ignoreDismissed: true,
        namespace: 'comingSoon',
      })
    },

    /**
     * Handles private sale click
     */
    handlePrivateSaleClick(event) {
      event.preventDefault()

      this.openOverlay({
        component: 'private-sale',
        ignoreDismissed: true,
        namespace: 'privateSale',
      })
    },

    /**
     * Clears the cart, then adds the new product in.
     */
    async emptyCartAddProduct() {
      try {
        await this.clearCart()
        this.showPreOrderConfirm = false
        this.handleAddToCart()
      } catch (error) {
        this.showPreOrderConfirm = false
        throw new Error(error)
      }
    },

    /**
     * Handles the emitted message hovered event from the btn element.
     * @param {Boolean} isHovered
     */
    showShippingMessage(isHovered) {
      this.shippingHover = isHovered
    },

    /**
     * Handles the emitted message hovered event from the btn element.
     * @param {Boolean} isHovered
     */
    showPreOrderMessage(isHovered) {
      this.preOrderHover = isHovered
    },

    /**
     * Handles the emitted message hovered event from the btn element.
     * @param {Boolean} isHovered
     */
    showComingSoonMessage(isHovered) {
      this.comingSoonHover = isHovered
    },

    /**
     * Deals with the main emitted event from the size selector component.
     * @param {Object} { action {string}, variant {Variant} }
     */
    handleSizeSelectorAction({ action = '', variant = null } = {}) {
      if (!variant || !variant.id) {
        return
      }

      this.currentVariantId = variant.id
      this.$store.dispatch('product/setVariant', variant)

      switch (action) {
        case 'notify-me':
          this.handleNotifyUpdate(variant, true)
          this.formFooterState = 'notifyMe'
          break
        case 'variant-updated':
          this.handleNotifyUpdate(variant, false)
          this.formFooterState = 'addToCart'
          break
        case 'open-size-guide':
          this.showSizeGuide = !this.showSizeGuide
          break
        default:
        // Do nothing
      }
    },

    /**
     * Get current variant on page load or product update.
     */
    getCurrentVariant() {
      if (!this.product || !this.product?.variants?.length) {
        return
      }

      if (this.hasOnlyDefaultVariant) {
        this.currentVariantId = this.product.variants[0].id
        return
      }

      // If there is only one variant, select it.
      if (this.hasOneVariant() && this.product.variants[0].available) {
        setTimeout(() => {
          this.handleSizeSelectorAction({
            action: 'variant-updated',
            variant: this.product.variants[0],
          })
        }, 200)
      }

      const variant = this.getVariantFromUrl()
      if (!variant || (this.isMobile && !variant?.available)) {
        this.currentVariantId = null
        return
      }

      if (!this.isMobile && !variant?.available) {
        this.formFooterState = 'notifyMe'
      }
      this.currentVariantId = variant.id
    },

    /**
     * Check if the product has one variant
     */
    hasOneVariant() {
      return this.product?.variants?.length === 1
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1))
      const variantId = Number(params.get('variant'))
      return this.getVariantFromId(variantId)
    },

    /**
     * Get variant object from variant ID.
     * @returns {Object}
     */
    getVariantFromId(variantId) {
      return this.product?.variants?.find((variant) => {
        return variant.id === variantId
      })
    },

    /**
     * Handle quantity selector update.
     * @param {String} quantity - New quantity.
     */
    handleQuantityUpdate(quantity) {
      this.quantity = quantity
    },

    /**
     * Handles the emitted event from mobile size selector when notify is
     * selected.
     * @param {Object} variant - Shopify variant object.
     * @param {Boolean} state - Defaults to true, updates the notify UI state.
     */
    handleNotifyUpdate(variant, state = true) {
      this.currentVariantId = variant?.id

      if (state) {
        this.notifyVariant = variant
        return
      }

      this.currentVariantId = null

      if (!this.isMobile) {
        return
      }
      this.formFooterState = 'selectSize'
    },

    /**
     * Event emmiter for the 'mobile-size-selector' component.
     */
    handleAtcClick() {
      cnvs.EventBus.emit('atc-clicked')
    },

    /**
     * Checks if there are any preorder products in the cart before allowing
     * add-to-cart to continue.
     */
    addToCartValidation() {
      if (!this.isVariantSelected || this.state !== 'ready') {
        return
      }

      cnvs.EventBus.emit('atc-clicked')

      if (this.preOrderProduct) {
        if (this.cart?.response?.items?.length && this.hasPreOrderProduct) {
          this.handleAddToCart()
          return
        }

        this.showPreOrderConfirm = true
        return
      }

      if (this.cart?.response?.items?.length && this.hasPreOrderProduct) {
        this.showPreOrderConfirm = true
        return
      }

      this.handleAddToCart()
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (!this.variant || this.state !== 'ready') {
        return
      }

      window.dpc.sendAddToCartEvent({ product_id: `[${this.variant.id}]` })

      clearTimeout(this.timeout)
      this.state = 'adding'

      const lineItemProperties = { ...this.lineItemProperties }

      const items = [
        {
          id: this.variant.id,
          properties: lineItemProperties,
          quantity: this.quantity,
        },
      ]

      try {
        await this.addItem(items)
        this.state = 'added'

        if (this.getComponentExists('cart-drawer')) {

          // set cart in vuex
          fetch('/cart.js')
            .then((response) => response.json())
            .then((data) => {
              this.$store.dispatch('cart/setStoryResponse', data)
            })

          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })

          // Show site-header
          if (this.isMobile) {
            document.documentElement.setAttribute('data-scrolldir', 'up')
          }
        }

        await this.resetState()

      } catch (error) {
        if (error.description) {
          this.cartError = error
        }

        reportError('Failed to add product to cart', error)
      }
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await new Promise((resolve) => {
        setTimeout(resolve, this.$timing('message'))
      })

      this.state = 'ready'
      this.quantity = 1
      this.showSizeGuide = false
      this.showMobileSizeSelector = false

      if (!this.hasOneVariant()) {
        this.currentVariantId = null
      }

      if (!this.isMobile) {
        this.$refs.sizeSelector.resetComponent()
        return
      }

      if (!this.hasOneVariant()) {
        this.formFooterState = 'addToCart'
      }

      this.$refs.mobileSizeSelector?.resetComponent()
    },
  },
}
</script>

<style lang="scss">
@import './product-form';
</style>
